import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  CLIENT_WRITABLE_GROWTH_MEASUREMENT_TYPES,
  decide,
  screeningRiskLevelLabel,
  screeningToolLabel,
  SCREENING_RISK_LEVELS,
  SCREENING_TOOLS,
  type BillingSuggestionEnvelope,
  type BrightFuturesEnvelope,
  type DevelopmentalScreeningResponse,
  type GrowthMeasurementResponse,
  type ImmunizationGapEnvelope,
} from '@emr/shared';

import {
  createDevelopmentalScreening,
  createGrowthMeasurement,
  fetchBillingSuggestions,
  fetchBrightFuturesSchedule,
  fetchDevelopmentalScreenings,
  fetchGrowthMeasurements,
  fetchImmunizationGaps,
} from '../api/pediatrics.ts';
import { useGrants } from '../auth/sessionContext.ts';
import { Button, ErrorText, Field, Panel, SelectField } from '../components/primitives.tsx';
import { GatedRoute } from '../components/GatedRoute.tsx';
import { QuerySection } from '../components/QuerySection.tsx';
import { FailureNotice } from '../components/AsyncState.tsx';

/**
 * The pediatrics module's real, patient-scoped surface (`BACKLOG.md` M8). Reachable directly by URL
 * even with the nav hidden, per `PatientDermatologyPage.tsx`'s/`PatientAestheticsPage.tsx`'s own
 * stated reasoning: a route cannot rely on nav visibility, so the gate is re-applied here via
 * `GatedRoute`.
 *
 * Five sections: growth measurements (with a computed LMS percentile — UNREVIEWED reference table,
 * flagged in the UI itself, not just in code comments), developmental/autism screening results (no
 * instrument content — `@emr/shared`'s `developmentalScreening.ts` header), an immunization "not yet
 * recorded" gap list (reads the existing core immunizations tab's own data, not a duplicate), the
 * Bright Futures periodicity schedule, and billing-code suggestions.
 */
export function PatientPediatricsPage() {
  const { t } = useTranslation('pediatrics');
  const { patientId } = useParams<{ patientId: string }>();

  if (patientId === undefined) {
    return <FailureNotice error={new Error('No patient id in the URL.')} />;
  }

  return (
    <GatedRoute title={t('heading')} capability="chart:read" feature="pediatricsModule">
      <div className="flex flex-col gap-6">
        <GrowthSection patientId={patientId} />
        <DevelopmentalScreeningSection patientId={patientId} />
        <ImmunizationGapSection patientId={patientId} />
        <BrightFuturesSection patientId={patientId} />
        <BillingSuggestionsSection patientId={patientId} />
      </div>
    </GatedRoute>
  );
}

function SectionHeading({ children }: { readonly children: string }) {
  return <h2 className="m-0 text-lg font-semibold text-ink">{children}</h2>;
}

// --- Growth measurements -------------------------------------------------------------------------

function growthMeasurementTypeLabel(type: string, t: (key: string) => string): string {
  if (type === 'weight_kg') return t('growth.type.weightKg');
  if (type === 'length_height_cm') return t('growth.type.lengthHeightCm');
  if (type === 'head_circumference_cm') return t('growth.type.headCircumferenceCm');
  // Server-computed only (see @emr/shared's growthCharts.ts header) — never entered through the
  // form below, but a stored row can carry this type and needs a real label in the list.
  if (type === 'bmi') return t('growth.type.bmi');
  return type;
}

function GrowthSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('pediatrics');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['pediatrics', 'growth-measurements', patientId];

  const query = useQuery<readonly GrowthMeasurementResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchGrowthMeasurements(patientId, signal),
  });

  const [measurementType, setMeasurementType] = useState<string>(
    CLIENT_WRITABLE_GROWTH_MEASUREMENT_TYPES[0],
  );
  const [value, setValue] = useState('');
  const [measuredOn, setMeasuredOn] = useState('');

  const create = useMutation({
    mutationFn: () => {
      // BMI is never in this list — it is always server-computed from a same-encounter weight/height
      // pair, never entered directly. See @emr/shared's growthCharts.ts header.
      const parsedType = CLIENT_WRITABLE_GROWTH_MEASUREMENT_TYPES.find(
        (t2) => t2 === measurementType,
      );
      if (parsedType === undefined) throw new Error('invalid measurement type');
      return createGrowthMeasurement(patientId, {
        measurementType: parsedType,
        value: Number(value),
        measuredOn,
        confidentialityReasons: [],
      });
    },
    onSuccess: () => {
      setValue('');
      setMeasuredOn('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('growth.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('growth.unreviewedNotice')}</p>
      <p className="m-0 text-sm text-ink-muted">{t('growth.bmiComputedNotice')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('growth.empty')}
        >
          {(measurements) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {measurements.map((m) => (
                <li key={m.id} className="rounded-control border border-line p-3 text-sm">
                  <p className="m-0 font-medium text-ink">
                    {m.measuredOn} &mdash; {growthMeasurementTypeLabel(m.measurementType, t)}:{' '}
                    {m.value}
                  </p>
                  <p className="m-0 mt-1 text-ink-muted">
                    {m.percentile === null
                      ? t('growth.percentileUnavailable', {
                          reason: m.percentileUnavailableReason ?? 'unknown',
                        })
                      : t('growth.percentile', {
                          percentile: m.percentile.toFixed(1),
                          source: m.growthChartSource === 'who' ? 'WHO' : 'CDC',
                        })}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <SelectField
              label={t('growth.typeLabel')}
              value={measurementType}
              onChange={(event) => {
                setMeasurementType(event.target.value);
              }}
            >
              {CLIENT_WRITABLE_GROWTH_MEASUREMENT_TYPES.map((mt) => (
                <option key={mt} value={mt}>
                  {growthMeasurementTypeLabel(mt, t)}
                </option>
              ))}
            </SelectField>
            <Field
              label={t('growth.valueLabel')}
              type="number"
              step="any"
              min={0}
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
              }}
              required
              className="sm:max-w-[10ch]"
            />
            <Field
              label={t('growth.measuredOnLabel')}
              type="date"
              value={measuredOn}
              onChange={(event) => {
                setMeasuredOn(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={create.isPending}>
              {t('growth.recordButton')}
            </Button>
          </form>
        ) : null}
        {create.isError ? <ErrorText message={t('growth.recordError')} /> : null}
      </Panel>
    </section>
  );
}

// --- Developmental screening -----------------------------------------------------------------------

function DevelopmentalScreeningSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('pediatrics');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['pediatrics', 'developmental-screenings', patientId];

  const query = useQuery<readonly DevelopmentalScreeningResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchDevelopmentalScreenings(patientId, signal),
  });

  const [screeningTool, setScreeningTool] = useState<string>(SCREENING_TOOLS[0]);
  const [screenedOn, setScreenedOn] = useState('');
  const [score, setScore] = useState('');
  const [riskLevel, setRiskLevel] = useState<string>(SCREENING_RISK_LEVELS[0]);

  const create = useMutation({
    mutationFn: () => {
      const parsedTool = SCREENING_TOOLS.find((tool) => tool === screeningTool);
      const parsedRisk = SCREENING_RISK_LEVELS.find((risk) => risk === riskLevel);
      if (parsedTool === undefined || parsedRisk === undefined) {
        throw new Error('invalid screening tool or risk level');
      }
      return createDevelopmentalScreening(patientId, {
        screeningTool: parsedTool,
        screenedOn,
        score: score === '' ? undefined : Number(score),
        riskLevel: parsedRisk,
        confidentialityReasons: [],
      });
    },
    onSuccess: () => {
      setScreenedOn('');
      setScore('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('screening.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('screening.description')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('screening.empty')}
        >
          {(screenings) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {screenings.map((s) => (
                <li key={s.id} className="rounded-control border border-line p-3 text-sm">
                  <p className="m-0 font-medium text-ink">
                    {s.screenedOn} &mdash; {screeningToolLabel(s.screeningTool)} (
                    {t('screening.ageMonths', { count: s.ageMonthsAtScreening })})
                  </p>
                  <p className="m-0 mt-1 text-ink-muted">
                    {screeningRiskLevelLabel(s.riskLevel)}
                    {s.score === null ? '' : t('screening.scoreSuffix', { score: s.score })}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <SelectField
              label={t('screening.toolLabel')}
              value={screeningTool}
              onChange={(event) => {
                setScreeningTool(event.target.value);
              }}
            >
              {SCREENING_TOOLS.map((tool) => (
                <option key={tool} value={tool}>
                  {screeningToolLabel(tool)}
                </option>
              ))}
            </SelectField>
            <Field
              label={t('screening.screenedOnLabel')}
              type="date"
              value={screenedOn}
              onChange={(event) => {
                setScreenedOn(event.target.value);
              }}
              required
            />
            <Field
              label={t('screening.scoreLabel')}
              type="number"
              value={score}
              onChange={(event) => {
                setScore(event.target.value);
              }}
              hint={t('screening.scoreHint')}
              className="sm:max-w-[10ch]"
            />
            <SelectField
              label={t('screening.riskLevelLabel')}
              value={riskLevel}
              onChange={(event) => {
                setRiskLevel(event.target.value);
              }}
            >
              {SCREENING_RISK_LEVELS.map((risk) => (
                <option key={risk} value={risk}>
                  {screeningRiskLevelLabel(risk)}
                </option>
              ))}
            </SelectField>
            <Button type="submit" pending={create.isPending}>
              {t('screening.recordButton')}
            </Button>
          </form>
        ) : null}
        {create.isError ? <ErrorText message={t('screening.recordError')} /> : null}
      </Panel>
    </section>
  );
}

// --- Immunization gap list --------------------------------------------------------------------------

function ImmunizationGapSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('pediatrics');
  const query = useQuery<ImmunizationGapEnvelope>({
    queryKey: ['pediatrics', 'immunization-gaps', patientId],
    queryFn: ({ signal }) => fetchImmunizationGaps(patientId, signal),
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('immunizationGaps.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('immunizationGaps.description')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.missing.length === 0}
          emptyMessage={t('immunizationGaps.empty')}
        >
          {(data) => (
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {data.missing.map((entry) => (
                <li key={entry.antigen} className="text-sm text-ink">
                  {entry.label}
                </li>
              ))}
            </ul>
          )}
        </QuerySection>
      </Panel>
    </section>
  );
}

// --- Bright Futures schedule ------------------------------------------------------------------------

function BrightFuturesSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('pediatrics');
  const query = useQuery<BrightFuturesEnvelope>({
    queryKey: ['pediatrics', 'bright-futures', patientId],
    queryFn: ({ signal }) => fetchBrightFuturesSchedule(patientId, signal),
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('brightFutures.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('brightFutures.description')}</p>
      <Panel>
        <QuerySection query={query} loadingLabel={t('common.loading')}>
          {(data) => (
            <div className="flex flex-col gap-3 text-sm">
              <p className="m-0 text-ink">
                {t('brightFutures.nearest')}{' '}
                <span className="font-medium">
                  {data.nearestVisit === null
                    ? t('brightFutures.noNearest')
                    : data.nearestVisit.label}
                </span>
              </p>
              {data.upcomingVisits.length === 0 ? (
                <p className="m-0 text-ink-muted">{t('brightFutures.noneUpcoming')}</p>
              ) : (
                <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
                  {data.upcomingVisits.slice(0, 6).map((visit) => (
                    <li
                      key={visit.id}
                      className="rounded-pill bg-canvas px-2 py-0.5 text-xs text-ink-muted"
                    >
                      {visit.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </QuerySection>
      </Panel>
    </section>
  );
}

// --- Billing-code suggestions -----------------------------------------------------------------------

function BillingSuggestionsSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('pediatrics');
  const [newPatient, setNewPatient] = useState(false);
  const [vaccineComponentCount, setVaccineComponentCount] = useState('');

  const query = useQuery<BillingSuggestionEnvelope>({
    queryKey: ['pediatrics', 'billing-suggestions', patientId, newPatient, vaccineComponentCount],
    queryFn: ({ signal }) =>
      fetchBillingSuggestions(
        patientId,
        {
          newPatient,
          vaccineComponentCount:
            vaccineComponentCount === '' ? undefined : Number(vaccineComponentCount),
        },
        signal,
      ),
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('billing.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('billing.unreviewedNotice')}</p>
      <Panel>
        <div className="mb-4 flex flex-col gap-3 border-b border-row-line pb-4 sm:flex-row sm:items-end sm:gap-4">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={newPatient}
              onChange={(event) => {
                setNewPatient(event.target.checked);
              }}
            />
            {t('billing.newPatientLabel')}
          </label>
          <Field
            label={t('billing.vaccineComponentCountLabel')}
            type="number"
            min={0}
            value={vaccineComponentCount}
            onChange={(event) => {
              setVaccineComponentCount(event.target.value);
            }}
            className="sm:max-w-[12ch]"
          />
        </div>
        <QuerySection query={query} loadingLabel={t('common.loading')}>
          {(data) => (
            <div className="flex flex-col gap-3 text-sm">
              <p className="m-0 text-ink">
                {t('billing.preventiveEmCode')}{' '}
                <span className="font-medium">
                  {data.preventiveEmCode === null
                    ? t('billing.none')
                    : `${data.preventiveEmCode.cpt} — ${data.preventiveEmCode.display}`}
                </span>
              </p>
              {data.vaccineAdministrationCodes !== null ? (
                <div>
                  <p className="m-0 text-ink">{t('billing.vaccineAdministrationCodes')}</p>
                  <ul className="m-0 mt-1 flex list-none flex-col gap-0.5 p-0 text-ink-muted">
                    {data.vaccineAdministrationCodes.map((line) => (
                      <li key={line.cpt}>
                        {line.cpt} &mdash; {line.display}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div>
                <p className="m-0 text-ink">{t('billing.developmentalScreeningCodes')}</p>
                <ul className="m-0 mt-1 flex list-none flex-col gap-0.5 p-0 text-ink-muted">
                  {data.developmentalScreeningCodes.map((line) => (
                    <li key={line.cpt}>
                      {line.cpt} &mdash; {line.display}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </QuerySection>
      </Panel>
    </section>
  );
}
